import * as React from 'react';
import { useState } from 'react';
import { Button, Tag } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import { withUserType } from '../../../components/withUserType';
import { withCurrentUser } from '../../../hooks/withCurrentUser';
import { cn } from '../../../utils/ClassNamesUtils';
import { nullSafeGetOrElse } from '../../../utils/DataAccessUtils';
import { OSCAR_MARK_SIZE } from '../../constants/AIAgentConstants';
import { OscarRoleProvider, useOscarTheme } from '../../context/OscarRoleContext';
import useAgentChatEnabled from '../../hooks/useAgentChatEnabled';
import { requestOscarPanelOpen } from '../../utils/OscarPanelEvents';
import OscarMark from '../shared/OscarMark';

/**
 * The accounts that have dismissed the banner, in `localStorage` so it stays
 * dismissed across reloads and sign-ins. Read on mount only: a tab already open
 * keeps the banner until it next loads, which is fine for a one-off
 * announcement. Keyed by email because sign-out
 * only clears `USER_LOCALSTORAGE_KEYS`: a single flag would carry one person's
 * dismissal over to the next account signed in on the same browser.
 */
const DISMISSED_KEY = 'oscar.betaBannerDismissedBy';

const readDismissedEmails = (): string[] => {
	try {
		const raw = window.localStorage.getItem(DISMISSED_KEY);
		const parsed = raw ? JSON.parse(raw) : [];
		return Array.isArray(parsed) ? parsed : [];
	} catch (storageError) {
		return [];
	}
};

const readDismissed = (email: string): boolean => !!email && readDismissedEmails().includes(email);

const storeDismissed = (email: string): void => {
	if (!email || readDismissed(email)) {
		return;
	}
	try {
		window.localStorage.setItem(DISMISSED_KEY, JSON.stringify([...readDismissedEmails(), email]));
	} catch (storageError) {
		// Private-mode Safari: the banner just reappears on the next load.
	}
};

interface OscarBetaBannerProps {
	/** Injected by `withUserType` — `state.session.userType`. */
	userType?: string;
	/** Injected by `withCurrentUser` — `state.session.currentUser`. */
	currentUser?: { email?: string };
}

const OscarBetaBannerContent = ({ onDismiss }: { onDismiss: () => void }) => {
	const theme = useOscarTheme();

	// Trying Oscar is what the banner is for, so the CTA retires it too.
	const openOscar = () => {
		requestOscarPanelOpen();
		onDismiss();
	};

	return (
		<div
			role="region"
			aria-label="Oscar beta announcement"
			className={cn(
				'flex items-center gap-s px-m py-xs print:hidden',
				theme.surface,
				theme.stroke,
				'border-x-0 border-t-0'
			)}
		>
			<OscarMark size={OSCAR_MARK_SIZE.banner} />
			<div className="flex min-w-0 flex-1 flex-wrap items-center gap-x-xs">
				<span className="bodymedium text-character-primary">Meet Oscar, your AI assistant.</span>
				<Tag className="!mr-0">Beta</Tag>
				<span className="bodyregular hidden text-character-secondary sm:inline">
					Oscar doesn't just answer questions. It gets work done.
				</span>
				{/* Phones: under the title, as the row has no room beside the CTA. */}
				<span className="footnoteregular w-full text-character-secondary sm:hidden">
					Access to admins only
				</span>
			</div>
			<span className="footnoteregular hidden text-character-secondary sm:inline">
				Access to admins only
			</span>
			<Button type="primary" onClick={openOscar}>
				Try Oscar
			</Button>
			<Button
				type="text"
				aria-label="Dismiss Oscar announcement"
				icon={<CloseOutlined />}
				onClick={onDismiss}
			/>
		</div>
	);
};

/**
 * Announces the Oscar beta across the top of the app. Shown only to buyers with
 * the `agent-chat` flag, until they dismiss it or open Oscar from it.
 *
 * Mount it only where `OscarDock` is mounted: the CTA asks the dock to open,
 * and with no dock there is nothing to answer.
 */
const OscarBetaBannerBase = ({ userType, currentUser }: OscarBetaBannerProps) => {
	const enabled = useAgentChatEnabled(userType);
	const userEmail = nullSafeGetOrElse('email', currentUser, '');
	const [dismissed, setDismissed] = useState(() => readDismissed(userEmail));

	if (!enabled || dismissed) {
		return null;
	}

	const dismiss = () => {
		storeDismissed(userEmail);
		setDismissed(true);
	};

	return (
		<OscarRoleProvider userType={userType}>
			<OscarBetaBannerContent onDismiss={dismiss} />
		</OscarRoleProvider>
	);
};

export const OscarBetaBanner = withUserType(withCurrentUser(OscarBetaBannerBase));

export default OscarBetaBanner;
